import React, { useState } from 'react';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { useAppContext } from '../hooks/useAppContext';
import Terms from './Terms';
import PrivacyPolicy from './PrivacyPolicy';
import './LoginPage.css';
import icon from '../assets/icon.png';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // FIXED: Separate loading states for each authentication method
  const [emailSignInLoading, setEmailSignInLoading] = useState(false);
  const [emailSignUpLoading, setEmailSignUpLoading] = useState(false);
  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);
  const [googleSignUpLoading, setGoogleSignUpLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [currentView, setCurrentView] = useState<'auth' | 'terms' | 'privacy'>('auth');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  
  const { 
    signInWithGoogle, 
    signInWithEmail, 
    signUpWithEmail,
    resendVerificationEmail,
    signInAsAdmin,
    user,
    error 
  } = useFirebaseAuth();
  
  const { showToast } = useAppContext();

  // Check for pending toasts and email verification status
  React.useEffect(() => {
    console.log('🔍 LoginPage: Checking for pending messages...');
    
    // Check if user returned from email verification
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('verified') === 'true') {
      console.log('✅ User returned from email verification');
      showToast({
        type: 'success',
        title: 'Email Verified!',
        message: 'Your email has been verified. You can now sign in with your credentials.',
        duration: 6000
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Clean up the needs_email_verification flag now that user is on login page
    const needsVerification = localStorage.getItem('needs_email_verification') === 'true';
    
    console.log('🔍 Needs verification flag:', needsVerification);
    
    if (needsVerification) {
      console.log('🧹 Cleaning up needs_email_verification flag');
      localStorage.removeItem('needs_email_verification');
    }
  }, [showToast]);

  const handleGoogleSignIn = async () => {
    setGoogleSignInLoading(true);
    try {
      await signInWithGoogle(false);
    } finally {
      setGoogleSignInLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setGoogleSignUpLoading(true);
    try {
      await signInWithGoogle(true);
    } finally {
      setGoogleSignUpLoading(false);
    }
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    
    setEmailSignInLoading(true);
    try {
      // Check if this is admin login
      if (email === 'admin') {
        console.log('🔑 Admin login detected, using direct authentication');
        const success = await signInAsAdmin(email, password);
        if (success) {
          console.log('✅ Admin login successful');
        }
      } else {
        // Regular Firebase email/password login
        const success = await signInWithEmail({ email, password });
        // If sign-in failed due to email verification, show resend option
        if (!success && error && error.includes('verify your email')) {
          setShowResendVerification(true);
        }
      }
    } finally {
      setEmailSignInLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !displayName || !confirmPassword) return;
    
    if (password !== confirmPassword) {
      showToast({
        type: 'error',
        title: 'Password Mismatch',
        message: 'Please make sure your passwords match'
      });
      return;
    }
    
    setEmailSignUpLoading(true);
    try {
      console.log('🔄 Starting signup process...');
      const result = await signUpWithEmail({ email, password, displayName });
      console.log('📋 Signup result:', result);
      
      if (result.success && result.requiresVerification) {
        // Set flag to ensure user stays on login page
        localStorage.setItem('needs_email_verification', 'true');
        
        // Show toast immediately and store flag as backup
        const toastData = {
          type: 'success' as const,
          title: 'Account Created Successfully!',
          message: 'Please check your email (including spam folder) and click the verification link before signing in.',
          duration: 10000
        };
        
                showToast(toastData);
        console.log('✅ Account created, showed toast and switching to sign-in');
        
        // Clear form and switch to sign-in
        clearForm();
        setShowSignUp(false);
      } else {
        console.log('❌ Signup failed or no verification required:', result);
      }
    } finally {
      setEmailSignUpLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email || !password) {
      showToast({
        type: 'error',
        title: 'Email and Password Required',
        message: 'Please enter your email and password to resend verification'
      });
      return;
    }
    
    setResendLoading(true);
    try {
      const success = await resendVerificationEmail(email, password);
      if (success) {
        showToast({
          type: 'success',
          title: 'Verification Email Sent',
          message: 'Please check your email (including spam folder) for the verification link.',
          duration: 8000
        });
        setShowResendVerification(false);
      }
    } finally {
      setResendLoading(false);
    }
  };

  const clearForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setConfirmPassword('');
    setShowResendVerification(false);
  };

  // Cleanup pending toasts if user navigates away without completing flow
  React.useEffect(() => {
    return () => {
      // Only clean up if user is authenticated (completed the flow)
      const pendingToast = localStorage.getItem('pending_toast');
      if (pendingToast && user) {
        localStorage.removeItem('pending_toast');
      }
    };
  }, [user]);

  const goToSignUp = () => {
    setShowSignUp(true);
    clearForm();
  };

  const goToSignIn = () => {
    setShowSignUp(false);
    clearForm();
  };

  // Helper to check if any authentication is in progress
  const isAnyAuthInProgress = emailSignInLoading || emailSignUpLoading || googleSignInLoading || googleSignUpLoading;

  // Handle showing Terms or Privacy Policy
  if (currentView === 'terms') {
    return <Terms onBack={() => setCurrentView('auth')} />;
  }

  if (currentView === 'privacy') {
    return <PrivacyPolicy onBack={() => setCurrentView('auth')} />;
  }

  if (showSignUp) {
    return (
      <div className="app">
        <div className="main-layout">
          <div className="login-container">
            <div className="login-card">
              {/* Header */}
              <div className="login-header">
                <div className="login-brand">
                  <img 
                    src={icon} 
                    alt="ppl.contact" 
                    className="login-icon"
                  />
                  <h1 className="login-title">
                    ppl.contact
                  </h1>
                </div>
              </div>

              {/* Sign Up Form */}
              <form 
                onSubmit={handleEmailSignUp}
                className="auth-form"
              >
                <div className="form-group">
                  <label htmlFor="signup-name">Full Name</label>
                  <input
                    id="signup-name"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your full name"
                    required
                    disabled={isAnyAuthInProgress}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="signup-email">Email</label>
                  <input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={isAnyAuthInProgress}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="signup-password">Password</label>
                  <input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    required
                    disabled={isAnyAuthInProgress}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm-password">Confirm Password</label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    disabled={isAnyAuthInProgress}
                    className={`form-input ${confirmPassword && password !== confirmPassword ? 'error' : ''}`}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <div className="error-text">Passwords do not match</div>
                  )}
                </div>

                <button 
                  type="submit"
                  disabled={emailSignUpLoading || !email || !password || !displayName || !confirmPassword || password !== confirmPassword}
                  className="primary-button"
                >
                  {emailSignUpLoading ? (
                    <>
                      <div className="loading-spinner" />
                      Creating account...
                    </>
                  ) : (
                    'Sign Up'
                  )}
                </button>
              </form>

              {/* Google Sign Up */}
              <div className="google-section">
                <button 
                  onClick={handleGoogleSignUp}
                  disabled={isAnyAuthInProgress}
                  className="google-button"
                >
                  {googleSignUpLoading ? (
                    <>
                      <div className="loading-spinner" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      <svg className="google-icon" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Sign up with Google
                    </>
                  )}
                </button>
              </div>

              {/* Back to Sign In */}
              <div className="auth-footer">
                <p className="auth-footer-text">
                  Already have an account?{' '}
                  <button 
                    className="auth-link"
                    onClick={goToSignIn}
                    disabled={isAnyAuthInProgress}
                  >
                    Sign in
                  </button>
                </p>
              </div>

              {/* Terms and Privacy Policy Agreement */}
              <div className="terms-agreement">
                <p className="terms-text">
                  By signing up, you agree to our{' '}
                  <button 
                    className="terms-link"
                    onClick={() => setCurrentView('terms')}
                  >
                    Terms
                  </button>
                  {' '}and{' '}
                  <button 
                    className="terms-link"
                    onClick={() => setCurrentView('privacy')}
                  >
                    Privacy Policy
                  </button>
                </p>
              </div>

              {error && (
                <div className="error-message">
                  ⚠️ {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="main-layout">
        <div className="login-container">
          <div className="login-card">
            {/* Header */}
            <div className="login-header">
              <div className="login-brand">
                <img 
                  src={icon} 
                  alt="ppl.contact" 
                  className="login-icon"
                />
                <h1 className="login-title">
                  ppl.contact
                </h1>
              </div>
            </div>

            {/* Sign In Form */}
            <form 
              onSubmit={handleEmailSignIn}
              className="auth-form"
            >
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type={email === 'admin' ? 'text' : 'email'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={isAnyAuthInProgress}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={isAnyAuthInProgress}
                  className="form-input"
                />
              </div>

              <button 
                type="submit"
                disabled={emailSignInLoading || !email || !password}
                className="primary-button"
              >
                {emailSignInLoading ? (
                  <>
                    <div className="loading-spinner" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>



            {/* Resend Verification Email */}
            {showResendVerification && (
              <div className="verification-section">
                <p className="verification-text">
                  Need to verify your email?
                </p>
                <button 
                  onClick={handleResendVerification}
                  disabled={resendLoading || !email || !password}
                  className="resend-verification-button"
                >
                  {resendLoading ? (
                    <>
                      <div className="loading-spinner" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </button>
              </div>
            )}

            {/* Google Sign In */}
            <div className="google-section">
              <button 
                onClick={handleGoogleSignIn}
                disabled={isAnyAuthInProgress}
                className="google-button"
              >
                {googleSignInLoading ? (
                  <>
                    <div className="loading-spinner" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg className="google-icon" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </button>
            </div>

            {/* Divider */}
            <div className="auth-divider">
              <span>or</span>
            </div>

            {/* Sign Up Section */}
            <div className="signup-section">
              <button 
                onClick={goToSignUp}
                disabled={isAnyAuthInProgress}
                className="secondary-button"
              >
                Sign up with email & password
              </button>

              <div className="google-section">
                <button 
                  onClick={handleGoogleSignUp}
                  disabled={isAnyAuthInProgress}
                  className="google-button"
                >
                  {googleSignUpLoading ? (
                    <>
                      <div className="loading-spinner" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      <svg className="google-icon" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Sign up with Google
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Terms and Privacy Policy Agreement */}
            <div className="terms-agreement">
              <p className="terms-text">
                By signing in, you agree to our{' '}
                <button 
                  className="terms-link"
                  onClick={() => setCurrentView('terms')}
                >
                  Terms
                </button>
                {' '}and{' '}
                <button 
                  className="terms-link"
                  onClick={() => setCurrentView('privacy')}
                >
                  Privacy Policy
                </button>
              </p>
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;